import {
  getFrozenVisualViewportSnapshot,
  KEYBOARD_OPEN_DATA_ATTR_THRESHOLD_PX,
  registerVisualViewportFreezeListener,
  resolveVisualViewportSnapshot,
} from "./freeze-visual-viewport";

export interface VisualViewportCssSnapshot {
  visualVhPx: number;
  offsetTopPx: number;
  keyboardOffsetPx: number;
}

function isVisualViewportKeyboardOpen(snapshot: VisualViewportCssSnapshot): boolean {
  const visualViewportShrinkPx = Math.max(0, window.innerHeight - snapshot.visualVhPx);
  return (
    snapshot.keyboardOffsetPx > KEYBOARD_OPEN_DATA_ATTR_THRESHOLD_PX ||
    visualViewportShrinkPx > KEYBOARD_OPEN_DATA_ATTR_THRESHOLD_PX
  );
}

export function computeVisualViewportCss(
  height: number,
  offsetTop: number,
  innerHeight: number,
): VisualViewportCssSnapshot {
  const keyboardOffsetPx = Math.max(0, innerHeight - offsetTop - height);
  return {
    visualVhPx: height,
    offsetTopPx: offsetTop,
    keyboardOffsetPx,
  };
}

function readVisualViewportSnapshot(): VisualViewportCssSnapshot | null {
  const vv = window.visualViewport;
  if (!vv) return null;
  return computeVisualViewportCss(vv.height, vv.offsetTop ?? 0, window.innerHeight);
}

export function applyVisualViewportCss(
  style: CSSStyleDeclaration,
  snapshot: VisualViewportCssSnapshot,
): void {
  style.setProperty("--visual-vh", `${snapshot.visualVhPx}px`);
  style.setProperty("--visual-offset-top", `${snapshot.offsetTopPx}px`);
  style.setProperty("--keyboard-offset", `${snapshot.keyboardOffsetPx}px`);

  const html = style === document.documentElement.style ? document.documentElement : null;
  if (!html) return;

  if (getFrozenVisualViewportSnapshot()) {
    html.dataset.keyboardFrozen = "";
  } else {
    html.removeAttribute("data-keyboard-frozen");
  }

  if (isVisualViewportKeyboardOpen(snapshot)) {
    html.dataset.keyboardOpen = "";
  } else {
    html.removeAttribute("data-keyboard-open");
  }
}

export function syncVisualViewportCssProperties(): VisualViewportCssSnapshot | null {
  const raw = readVisualViewportSnapshot();
  if (!raw) return null;

  const snapshot = resolveVisualViewportSnapshot(raw, readVisualViewportSnapshot);
  applyVisualViewportCss(document.documentElement.style, snapshot);
  return snapshot;
}

registerVisualViewportFreezeListener(() => {
  const frozen = getFrozenVisualViewportSnapshot();
  if (frozen) {
    applyVisualViewportCss(document.documentElement.style, frozen);
  }
});
